import * as React from 'react';

import { Cell, type Table as TanstackTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';

import { TableBody, TableCell, TableRow } from '@/components/ui/table';

import type { TableVariants } from './types';
import { VirtualDataRow } from './virtual-data-row';

interface VirtualizedTableBodyProps<TData> {
  table: TanstackTable<TData>;
  isScrolled: boolean;
  onRowClick?: (row: TData) => void;
  /** Detail-route href for a row — enables CMD/CTRL+click and middle-click "open in new tab". */
  getRowHref?: (row: TData) => string | undefined;
  lastRowChildren?: React.ReactNode;
  getRowClassName?: (row: TData) => string | undefined;
  variant?: TableVariants;
  tableId?: string;
  filledColumnWidths?: Map<string, number>;
  registerRowRef?: (rowId: string, el: HTMLTableRowElement | null) => void;
  renderFullWidthRow?: (row: TData) => React.ReactNode | undefined;
  /** The `[data-scroll-container]` element the virtualizer measures/scrolls against. */
  scrollElementRef: React.RefObject<HTMLDivElement | null>;
  estimateRowHeight: number;
  /** Total table width (px) — used to give full-width rows an explicit width. */
  tableWidth?: number;
}

const OVERSCAN = 16;

/**
 * Virtualized body for `DataTable`'s sticky, non-grouped path: renders only the visible rows
 * (plus a small overscan) so very large tables stay responsive. Lives in its own file/component
 * so the `useVirtualizer` hook + its scroll listener never run for non-virtualized tables.
 *
 * Layout: the `<tbody>` becomes a `position: relative` block sized to the full virtual height;
 * each row is `position: absolute` + `display: flex` and each cell uses the SAME explicit width
 * the non-virtual path computes, promoted to `flex-basis`. This keeps body columns aligned with
 * the sticky `<thead>` (which keeps native `table-layout: fixed` widths) without relying on the
 * table flow that absolute rows would otherwise break.
 */
export function VirtualizedTableBody<TData>({
  table,
  isScrolled,
  onRowClick,
  getRowHref,
  lastRowChildren,
  getRowClassName,
  variant,
  tableId,
  filledColumnWidths,
  registerRowRef,
  renderFullWidthRow,
  scrollElementRef,
  estimateRowHeight,
  tableWidth,
}: VirtualizedTableBodyProps<TData>) {
  const rows = table.getRowModel().rows;

  // Signature of the current visible-column arrangement (order + which are shown). Rows are
  // memoized and TanStack reuses `row` identity, so without threading this into each row a
  // column reorder/hide would update the header but leave the memoized body cells stale.
  const columnArrangementKey = table
    .getVisibleLeafColumns()
    .map(column => column.id)
    .join(',');

  // Stable per-row ref callbacks (cached by row id) so memoized rows don't re-render every
  // scroll tick from a fresh ref identity. Each composes virtualizer.measureElement + the
  // nested-pagination observer registration.
  const rowRefCacheRef = React.useRef(new Map<string, (el: HTMLTableRowElement | null) => void>());

  const virtualizer = useVirtualizer({
    count: rows.length,
    // Null on the first renders (container mounts after) — react-virtual re-resolves it.
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => estimateRowHeight,
    // Larger overscan keeps rows mounted/measured ahead of a fast scroll, reducing the
    // brief "unmeasured row" flash. (Default `measureElement` — robust across browsers.)
    overscan: OVERSCAN,
    getItemKey: index => rows[index].id,
  });

  // NO manual `virtualizer.measure()` anywhere — react-virtual attaches a ResizeObserver to
  // every mounted row (via `measureElement`), so height changes from column-width reflow
  // (including live resize drags) update ONLY the affected rows' measurements. A blunt
  // `measure()` resets EVERY row to the uniform estimate and — because per-row ref callbacks
  // are cached and don't re-fire for already-mounted elements — heights stayed stuck at the
  // estimate: uniform rows, vanished dividers, jumped viewport. Do not reintroduce it.

  // Resolve a cell's explicit width using the same chain as the non-virtual path.
  // Always returns a number so it can drive both `width` and `flex-basis`. Memoized so it
  // stays reference-stable across scroll renders → memoized rows can bail out of re-rendering.
  const getCellWidth = React.useCallback(
    (cell: Cell<TData, unknown>): number => {
      const filled = tableId ? filledColumnWidths?.get(cell.column.id) : undefined;
      if (filled !== undefined) return filled;
      if (tableId && cell.column.id === 'select') {
        return cell.column.columnDef.size ?? cell.column.getSize();
      }
      return cell.column.getSize();
    },
    [tableId, filledColumnWidths]
  );

  // Empty: render a normal (non-virtual) tbody carrying the empty-state row.
  if (rows.length === 0) {
    return (
      <TableBody className="bg-background">
        <>{lastRowChildren}</>
      </TableBody>
    );
  }

  // The <tbody> is the positioning context: a block sized to the full virtual height, with each
  // row absolutely positioned inside it. (Rows are direct children of <tbody> — valid markup.)
  return (
    <TableBody
      className="block bg-background"
      // `display: block` tbody doesn't auto-stretch to the table width, and `width: 100%`
      // doesn't reliably resolve against a block-level tbody inside a <table> — use the
      // EXPLICIT pixel table width. Rows get the same explicit width: left-pinned sticky
      // cells can only slide within their row box, so the row must span the full scrollable
      // width or pinning silently degrades to normal flow.
      style={{
        position: 'relative',
        width: tableWidth ?? '100%',
        height: virtualizer.getTotalSize(),
      }}
    >
      {virtualizer.getVirtualItems().map(virtualItem => {
        const row = rows[virtualItem.index];
        const customRowClassName = getRowClassName?.(row.original);
        const fullWidthContent = renderFullWidthRow?.(row.original);

        // Composed ref (cached by row id so it's identity-stable across scroll renders): the
        // virtualizer measures the row AND the nested-pagination observer registers it.
        let setRowRef = rowRefCacheRef.current.get(row.id);
        if (!setRowRef) {
          setRowRef = (el: HTMLTableRowElement | null) => {
            virtualizer.measureElement(el);
            registerRowRef?.(row.id, el);
          };
          rowRefCacheRef.current.set(row.id, setRowRef);
        }

        if (fullWidthContent !== undefined) {
          // Full-width rows (e.g. the load-more spinner) use BLOCK layout with an EXPLICIT
          // pixel width (the total table width) — not `width: 100%`, which doesn't reliably
          // resolve against a `display: block` <tbody> inside a <table>. The explicit width
          // gives the cell's `w-full` content a definite box to center within.
          return (
            <TableRow
              key={row.id}
              ref={setRowRef}
              data-index={virtualItem.index}
              className={customRowClassName}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: tableWidth ?? '100%',
                display: 'block',
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <TableCell className="block w-full px-0">{fullWidthContent}</TableCell>
            </TableRow>
          );
        }

        return (
          <VirtualDataRow
            key={row.id}
            row={row}
            rowIndex={virtualItem.index}
            translateY={virtualItem.start}
            isScrolled={isScrolled}
            variant={variant}
            rowClassName={customRowClassName}
            onRowClick={onRowClick}
            getRowHref={getRowHref}
            getCellWidth={getCellWidth}
            // Passed as a PROP (not folded into `key`): rows are `React.memo`'d and TanStack
            // reuses `row` identity, so on a reorder/hide an unchanged prop set would keep the
            // stale, already-rendered cell order. A changed arrangement string busts the memo
            // and re-renders the row's cells in the new order — WITHOUT remounting (a key
            // change would remount every visible row and replay the mount opacity-fade).
            columnArrangementKey={columnArrangementKey}
            rowRef={setRowRef}
            rowWidth={tableWidth}
          />
        );
      })}
    </TableBody>
  );
}
